import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useAuth(); // Ensure auth context is available
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('completeProfile.errors.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }

    if (password.length < 8) {
      toast.error(
        t('completeProfile.errors.passwordTooShort', 'Password must be at least 8 characters'),
      );
      return;
    }

    // Validate age
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge =
      monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

    if (actualAge < 18) {
      toast.error(
        t(
          'completeProfile.errors.mustBe18',
          'You must be at least 18 years old to use this service',
        ),
      );
      return;
    }

    if (!gender) {
      toast.error(t('completeProfile.errors.genderRequired', 'Please select your gender'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/auth/phone/complete-signup', {
        email,
        password,
        name,
        dateOfBirth,
        gender,
      });

      // Update stored user data
      localStorage.setItem('user', JSON.stringify(response.data.user));

      toast.success(t('completeProfile.profileCompleted', 'Profile completed successfully!'));
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          t('completeProfile.errors.failedToComplete', 'Failed to complete profile'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8 pt-safe">
          {/* Icon */}
          <div className="flex justify-center mb-8 mt-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">✨</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text text-center mb-2">
            {t('completeProfile.title', 'Complete Your Profile')}
          </h1>

          {/* Subtitle */}
          <p className="text-dark-text-secondary text-center mb-8">
            {t('completeProfile.subtitle', 'Just a few more details to get started')}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-dark-text mb-2">
                {t('completeProfile.fullName', 'Full Name')}
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-white placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={t('completeProfile.fullNamePlaceholder', 'John Doe')}
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark-text mb-2">
                {t('completeProfile.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-white placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={t('completeProfile.emailPlaceholder', 'you@example.com')}
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="dateOfBirth"
                className="block text-sm font-medium text-dark-text mb-2"
              >
                {t('completeProfile.dateOfBirth', 'Date of Birth')}
              </label>
              <input
                id="dateOfBirth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-white placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-dark-text-tertiary">
                {t('completeProfile.ageRequirement', 'You must be 18 or older to use ChatNexus')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                {t('completeProfile.gender', 'Gender')}
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setGender('man')}
                  disabled={isLoading}
                  className={`py-3 px-4 rounded-xl border transition-colors ${
                    gender === 'man'
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-dark-card border-dark-border text-white hover:border-primary-500'
                  }`}
                >
                  {t('completeProfile.genderOptions.man', 'Man')}
                </button>
                <button
                  type="button"
                  onClick={() => setGender('woman')}
                  disabled={isLoading}
                  className={`py-3 px-4 rounded-xl border transition-colors ${
                    gender === 'woman'
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-dark-card border-dark-border text-white hover:border-primary-500'
                  }`}
                >
                  {t('completeProfile.genderOptions.woman', 'Woman')}
                </button>
                <button
                  type="button"
                  onClick={() => setGender('non-binary')}
                  disabled={isLoading}
                  className={`py-3 px-4 rounded-xl border transition-colors ${
                    gender === 'non-binary'
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-dark-card border-dark-border text-white hover:border-primary-500'
                  }`}
                >
                  {t('completeProfile.genderOptions.nonBinary', 'Non-binary')}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-text mb-2">
                {t('completeProfile.password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-white placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={t('completeProfile.passwordPlaceholder', 'Minimum 8 characters')}
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-dark-text mb-2"
              >
                {t('completeProfile.confirmPassword', 'Confirm Password')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-white placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={t(
                  'completeProfile.confirmPasswordPlaceholder',
                  'Re-enter your password',
                )}
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('completeProfile.creatingProfile', 'Creating profile...')
                : t('completeProfile.completeProfileButton', 'Complete Profile')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
